import axios from "axios";
import { toast } from "sonner";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth token lives in a secure httpOnly cookie set by the backend; the browser
// sends it automatically on same-origin requests. withCredentials enables that.
const client = axios.create({ baseURL: BASE, withCredentials: true });

let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};

client.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";
    if (status === 401 && !url.includes("/auth/login")) {
      if (onUnauthorized) onUnauthorized();
    } else if (status === 403) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "You do not have permission to do that");
    } else if (status === 429 && !url.includes("/auth/login")) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Too many attempts. Try again later.");
    }
    return Promise.reject(error);
  }
);

export const api = {
  // auth
  login: (email, password) =>
    client.post(`/auth/login`, { email, password }).then((r) => r.data),
  me: () => client.get(`/auth/me`).then((r) => r.data),
  logout: () => client.post(`/auth/logout`).then((r) => r.data),
  changePassword: (current_password, new_password, confirm_password) =>
    client.post(`/auth/change-password`, { current_password, new_password, confirm_password }).then((r) => r.data),
  forgotPassword: (email) => client.post(`/auth/forgot-password`, { email }).then((r) => r.data),
  validateResetToken: (token) => client.get(`/auth/reset-password/validate`, { params: { token } }).then((r) => r.data),
  resetPassword: (token, password) => client.post(`/auth/reset-password`, { token, password }).then((r) => r.data),
  // admin users
  listUsers: () => client.get(`/admin/users`).then((r) => r.data),
  unlockUser: (id) => client.post(`/admin/users/${id}/unlock`).then((r) => r.data),
  createUser: (data) => client.post(`/admin/users`, data).then((r) => r.data),
  updateUser: (id, data) => client.put(`/admin/users/${id}`, data).then((r) => r.data),
  resetUserPassword: (id, password) =>
    client.post(`/admin/users/${id}/reset-password`, { password }).then((r) => r.data),
  deleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),
  signoutUserSessions: (id, confirm = false) =>
    client.post(`/admin/users/${id}/signout-all`, { confirm }).then((r) => r.data),
  // audit + recycle bin (admin only)
  auditLogs: (params) => client.get(`/admin/audit-logs`, { params }).then((r) => r.data),
  auditSummary: () => client.get(`/admin/audit-summary`).then((r) => r.data),
  recycleBin: () => client.get(`/admin/recycle-bin`).then((r) => r.data),
  restoreRecord: (collection, id) =>
    client.post(`/admin/recycle-bin/${collection}/${id}/restore`).then((r) => r.data),
  permanentDelete: (collection, id, confirmation) =>
    client.post(`/admin/recycle-bin/${collection}/${id}/permanent-delete`, { confirmation }).then((r) => r.data),
  makeBackup: () => client.post(`/admin/backup`).then((r) => r.data),
  // system settings + backup/restore (admin only)
  settingsOverview: () => client.get(`/admin/settings/overview`).then((r) => r.data),
  listBackups: () => client.get(`/admin/backups`).then((r) => r.data),
  deleteBackup: (filename) => client.delete(`/admin/backups/${filename}`).then((r) => r.data),
  validateBackup: (filename) => client.post(`/admin/backups/validate`, { filename }).then((r) => r.data),
  validateUpload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/admin/backups/validate-upload`, fd).then((r) => r.data);
  },
  restorePreview: (filename) => client.post(`/admin/restore/preview`, { filename }).then((r) => r.data),
  restore: (payload) => client.post(`/admin/restore`, payload).then((r) => r.data),
  downloadBackup: async (filename) => {
    const res = await client.get(`/admin/backups/${filename}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  // domain CRUD
  list: (resource) => client.get(`/${resource}`).then((r) => r.data),
  get: (resource, id) => client.get(`/${resource}/${id}`).then((r) => r.data),
  create: (resource, data) => client.post(`/${resource}`, data).then((r) => r.data),
  update: (resource, id, data) => client.put(`/${resource}/${id}`, data).then((r) => r.data),
  remove: (resource, id, reason) =>
    client
      .delete(`/${resource}/${id}`, { params: reason ? { reason } : {} })
      .then((r) => r.data),
  reorder: (resource, ids) => client.post(`/${resource}/reorder`, { ids }).then((r) => r.data),
  summary: () => client.get(`/command-summary`).then((r) => r.data),
  productSummary: () => client.get(`/product-summary`).then((r) => r.data),
  launchSettings: () => client.get(`/launch-settings`).then((r) => r.data),
  updateLaunchSettings: (data) => client.put(`/launch-settings`, data).then((r) => r.data),
  updatesFor: (relatedId) =>
    client.get(`/updates`, { params: { related_id: relatedId } }).then((r) => r.data),
  rescheduleReview: (id, data) => client.post(`/innovations/${id}/reschedule-review`, data).then((r) => r.data),
  founderFocus: () => client.get(`/founder-focus`).then((r) => r.data),
  updateFounderFocus: (founder, data) =>
    client.put(`/founder-focus/${founder}`, data).then((r) => r.data),
  // office schedule
  officeSummary: (week_start) => client.get(`/office-schedule-summary`, { params: { week_start } }).then((r) => r.data),
  officeWeekOverview: () => client.get(`/office-week-overview`).then((r) => r.data),
  officeTransition: (id, status) => client.post(`/office-schedule/${id}/transition`, { status }).then((r) => r.data),
  officeCopyWeek: (source_week_start, exclude_ids) => client.post(`/office-schedule/copy-week`, { source_week_start, exclude_ids }).then((r) => r.data),
  // links
  links: () => client.get(`/links`).then((r) => r.data),
  createLink: (data) => client.post(`/links`, data).then((r) => r.data),
  updateLink: (id, data) => client.put(`/links/${id}`, data).then((r) => r.data),
  deleteLink: (id) => client.delete(`/links/${id}`).then((r) => r.data),
  // founder diary
  diaryEntries: () => client.get(`/diary-entries`).then((r) => r.data),
  createDiaryEntry: (data) => client.post(`/diary-entries`, data).then((r) => r.data),
  updateDiaryEntry: (id, data) => client.put(`/diary-entries/${id}`, data).then((r) => r.data),
  deleteDiaryEntry: (id) => client.delete(`/diary-entries/${id}`).then((r) => r.data),
  // weekly review
  weeklyReviews: (params) => client.get(`/weekly-reviews`, { params }).then((r) => r.data),
  weeklyReview: (id) => client.get(`/weekly-reviews/${id}`).then((r) => r.data),
  weeklyReviewCurrent: () => client.get(`/weekly-review-current`).then((r) => r.data),
  weeklyReviewSettings: () => client.get(`/weekly-review-settings`).then((r) => r.data),
  updateWeeklyReviewSettings: (data) => client.put(`/weekly-review-settings`, data).then((r) => r.data),
  generateWeeklyReview: (data) => client.post(`/weekly-reviews/generate`, data).then((r) => r.data),
  refreshWeeklyReview: (id) => client.post(`/weekly-reviews/${id}/refresh`).then((r) => r.data),
  updateWeeklyReview: (id, data) => client.put(`/weekly-reviews/${id}`, data).then((r) => r.data),
  weeklyReviewComment: (id, section, text) => client.post(`/weekly-reviews/${id}/comment`, { section, text }).then((r) => r.data),
  weeklyReviewTransition: (id, status, confirm = false) => client.post(`/weekly-reviews/${id}/transition`, { status, confirm }).then((r) => r.data),
  weeklyReviewCreatePriorities: (id) => client.post(`/weekly-reviews/${id}/create-priorities`).then((r) => r.data),
  deleteWeeklyReview: (id, reason) => client.delete(`/weekly-reviews/${id}`, { params: reason ? { reason } : {} }).then((r) => r.data),
  // daily tasks
  tasksSummary: () => client.get(`/tasks-summary`).then((r) => r.data),
  milestoneTaskSummary: (milestoneId) => client.get(`/tasks/milestone-summary/${milestoneId}`).then((r) => r.data),
  milestoneCompleteConfirm: (milestoneId) => client.post(`/tasks/milestone-complete-confirm/${milestoneId}`, {}).then((r) => r.data),
  taskTransition: (id, status) => client.post(`/tasks/${id}/transition`, { status }).then((r) => r.data),
  taskReschedule: (id, task_date) => client.post(`/tasks/${id}/reschedule`, { task_date }).then((r) => r.data),
  taskDuplicate: (id, task_date) => client.post(`/tasks/${id}/duplicate`, task_date ? { task_date } : {}).then((r) => r.data),
  taskComment: (id, text, mentions = []) => client.post(`/tasks/${id}/comment`, { text, mentions }).then((r) => r.data),
  // founder notebook
  notebookSummary: () => client.get(`/notebook-summary`).then((r) => r.data),
  noteMove: (id, column) => client.post(`/notes/${id}/move`, { column }).then((r) => r.data),
  noteReact: (id, emoji) => client.post(`/notes/${id}/react`, { emoji }).then((r) => r.data),
  notePin: (id, pinned) => client.post(`/notes/${id}/pin`, { pinned }).then((r) => r.data),
  noteArchive: (id, archived) => client.post(`/notes/${id}/archive`, { archived }).then((r) => r.data),
  noteComment: (id, text, mentions = []) => client.post(`/notes/${id}/comment`, { text, mentions }).then((r) => r.data),
  noteConvert: (id, target, data) => client.post(`/notes/${id}/convert`, { target, data }).then((r) => r.data),
  // notebook mentions
  mentions: () => client.get(`/notebook-mentions`).then((r) => r.data),
  mentionsUnreadCount: () => client.get(`/notebook-mentions/unread-count`).then((r) => r.data),
  mentionRead: (id, read) => client.post(`/notebook-mentions/${id}/read`, { read }).then((r) => r.data),
  mentionsMarkAllRead: (ids) => client.post(`/notebook-mentions/mark-all-read`, ids ? { ids } : {}).then((r) => r.data),
};
